import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { SettingsSection } from "./settingsLayout";
import type { StatusTone } from "./DiagnosticsSettings.types";

/**
 * What is left of Diagnostics' own primitive family after it moved onto the
 * settings standard.
 *
 * `Panel` and `PanelHeader` used to live here: a card with a 13px heading and
 * its own border, which made Diagnostics the one settings page that did not
 * look like its siblings. Headings are now `SettingsSection`, so the heading
 * scale, section spacing, and settings-search anchoring come from the same
 * place as every other page. What stayed is the part genuinely specific to
 * diagnostics — status tone, and the bordered surfaces dense evidence needs.
 */

export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function StatusDot({ tone }: { readonly tone: StatusTone }) {
  return (
    <span
      className={cn(
        "inline-block size-1.5 rounded-full",
        tone === "ready" ? "bg-success" : null,
        tone === "warning" ? "bg-warning" : null,
        tone === "error" ? "bg-destructive" : null,
        tone === "info" ? "bg-info" : null,
        tone === "muted" ? "bg-muted-foreground/45" : null,
      )}
    />
  );
}

export function StatusPill({ label, tone }: { readonly label: string; readonly tone: StatusTone }) {
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-md border px-2 text-[11px] font-medium",
        tone === "ready"
          ? "border-success-border bg-success-surface text-success-foreground"
          : null,
        tone === "warning"
          ? "border-warning-border bg-warning-surface text-warning-foreground"
          : null,
        tone === "error"
          ? "border-destructive-border bg-destructive-surface text-destructive-foreground"
          : null,
        tone === "info" ? "border-info-border bg-info-surface text-info-foreground" : null,
        tone === "muted" ? "border-border bg-muted/20 text-muted-foreground" : null,
      )}
    >
      <StatusDot tone={tone} />
      {label}
    </span>
  );
}

/**
 * A settings section carrying a diagnostics reading.
 *
 * The description sits under the heading rather than beside it because a
 * diagnostics subtitle is usually a sentence of evidence provenance, not a
 * label — and `SettingsSection` reserves its header row for the action.
 */
export function DiagnosticsSection({
  title,
  description,
  action,
  id,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly id?: string;
  readonly children?: ReactNode;
}) {
  return (
    <SettingsSection
      title={title}
      {...(id ? { id } : {})}
      {...(action ? { headerAction: action } : {})}
    >
      {description ? (
        <p className="px-3 pb-1 text-[13px] leading-[1.45] text-muted-foreground/80 sm:px-4">
          {description}
        </p>
      ) : null}
      {children}
    </SettingsSection>
  );
}

/**
 * The bordered surface dense evidence sits on inside a section.
 *
 * Settings rows are borderless because a label/control pair reads fine against
 * the page. A process table or a twenty-cell metric grid does not — the rows
 * need an edge to be a table rather than a run of text.
 */
export function DiagnosticsCard({
  className,
  children,
}: {
  readonly className?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className={cn("overflow-hidden rounded-xl border border-border/70 bg-card/40", className)}>
      {children}
    </div>
  );
}

/** A one-line note inside a card — an error, an empty state, a caveat. */
export function DiagnosticsNote({
  tone = "muted",
  children,
}: {
  readonly tone?: "muted" | "error";
  readonly children: ReactNode;
}) {
  return (
    <p
      className={cn(
        "px-4 py-3 text-sm",
        tone === "error" ? "text-destructive-foreground" : "text-muted-foreground",
      )}
    >
      {children}
    </p>
  );
}

export interface DiagnosticsMetric {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

/** The identity grid every target kind shows, in one place rather than three. */
export function DiagnosticsMetrics({
  metrics,
  className,
}: {
  readonly metrics: ReadonlyArray<DiagnosticsMetric>;
  readonly className?: string;
}) {
  if (metrics.length === 0) return null;
  // Every cell draws its own top and left hairline, and the grid is pulled a
  // pixel up and left so the first row's and first column's borders fall
  // outside the clipping container. The alternative — a 1px grid gap showing
  // a coloured container through — paints the *empty* slots of a part-filled
  // last row too, which reads as a grey cell with no data in it.
  return (
    <div className={cn("-mt-px -ml-px grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", className)}>
      {metrics.map((metric) => (
        <div key={metric.label} className="min-w-0 border-t border-l border-border/65 px-4 py-3">
          <div className="text-[11px] font-medium text-muted-foreground">{metric.label}</div>
          <div
            className={cn(
              "mt-1 truncate text-sm text-foreground",
              metric.mono ? "font-mono text-xs" : null,
            )}
          >
            {metric.value}
          </div>
        </div>
      ))}
    </div>
  );
}
